/**
 * Prompt `type` values used by the :7122 /api/Prompt endpoints. A prompt is keyed by
 * (tool, type, version): `tool` is always getCommonVisualizerType(), `type` picks the slot the
 * prompt belongs to and `version` is the revision inside that slot (one of them flagged
 * isDefault - see setDefaultPrompts).
 *
 * "First" / "Second" / "Third" are the three chained prompts the image render uses, managed on
 * the AI Management tab. "SqftAI" is the standalone prompt managed on the SQFT AI Management
 * tab - it is not sent by the image render, it is there for the SQFT Calculator to pick up.
 */
export const MAIN_PROMPT_TYPES = ["First", "Second", "Third"];

export const SQFT_AI_PROMPT_TYPE = "SqftAI";

/**
 * Every prompt type the dev popup manages. Used wherever all slots have to be walked - marking
 * the active prompt of each type after a fetch, and saving them all as their type's default.
 */
export const ALL_PROMPT_TYPES = [...MAIN_PROMPT_TYPES, SQFT_AI_PROMPT_TYPE];

/**
 * The group object properties holding a feature group's `enabled` / `sendThumbnail` flags for the
 * given prompt type.
 *
 * "First" / "Second" / "Third" share one pair ("enabled" / "sendThumbnail") - that is how the AI
 * Management tab has always worked, a toggle there is written to the active prompt part of all
 * three types, and the image render reads plain `enabled` off the group objects. "SqftAI" keeps
 * its own pair so the two tabs cannot overwrite each other's saved parts: a SqftAI prompt with no
 * parts yet would otherwise blank out `enabled` for every group the moment its tab is opened.
 */
export const getPromptFlagKeys = (promptType: string) => {
  return promptType === SQFT_AI_PROMPT_TYPE
    ? {enabledKey: "enabledSqftAI", sendThumbnailKey: "sendThumbnailSqftAI"}
    : {enabledKey: "enabled", sendThumbnailKey: "sendThumbnail"};
};

/** The prompt types a flag toggle on the given type has to be persisted to - see getPromptFlagKeys. */
export const getPromptTypesSharingFlags = (promptType: string) => {
  return promptType === SQFT_AI_PROMPT_TYPE ? [SQFT_AI_PROMPT_TYPE] : MAIN_PROMPT_TYPES;
};

/**
 * A group object's flag values for the given prompt type, falling back to the shared pair while
 * the type's own properties haven't been set yet - i.e. before its prompt parts have loaded, and
 * for synthetic rows such as the "_ColorOnly" ones, which are built with the shared defaults only.
 * For "First" / "Second" / "Third" the keys *are* the shared pair, so nothing changes for them.
 */
export const getPromptFlagValues = (groupObject: any, promptType: string) => {
  const {enabledKey, sendThumbnailKey} = getPromptFlagKeys(promptType);
  return {
    enabled: groupObject[enabledKey] === undefined ? groupObject.enabled : groupObject[enabledKey],
    sendThumbnail: groupObject[sendThumbnailKey] === undefined
      ? groupObject.sendThumbnail
      : groupObject[sendThumbnailKey],
  };
};
